import { useEffect, useId, useRef, type ComponentProps } from 'react'
import {
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  X,
} from 'lucide-react'
import type { NavigationAction } from './PlayerFooter'
import { Calendar } from './Calendar'
import { MobilePicker } from './MobilePicker'
import { addDays, dateLabel, monthOf } from './time'
import type { Camera } from './types'

interface CameraPickerProps {
  cameras?: Camera[]
  selected: string
  open: boolean
  mobile: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (id: string) => void
}

const cameraName = (camera: Camera) => camera.name

function CameraPicker({
  cameras,
  selected,
  open,
  mobile,
  onOpenChange,
  onSelect,
}: CameraPickerProps) {
  const id = useId()
  const control = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const camera = cameras?.find((c) => c.id === selected)
  useEffect(() => {
    if (!open || mobile) return
    const menu = control.current
    menu?.querySelector<HTMLButtonElement>('[aria-checked="true"]')?.focus()
    function dismiss(event: PointerEvent) {
      if (!menu?.contains(event.target as Node)) onOpenChange(false)
    }
    document.addEventListener('pointerdown', dismiss)
    return () => document.removeEventListener('pointerdown', dismiss)
  }, [open, mobile, onOpenChange])

  return (
    <div
      className="camera-control"
      ref={control}
      onBlur={(event) => {
        // Mobile dialogs manage focus themselves; Safari can report a null
        // relatedTarget while showModal moves focus.
        if (!mobile && !event.currentTarget.contains(event.relatedTarget))
          onOpenChange(false)
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault()
          event.stopPropagation()
          onOpenChange(false)
          trigger.current?.focus()
        }
      }}
    >
      <span className="camera-dot" aria-hidden="true" />
      <button
        className="camera-trigger"
        ref={trigger}
        aria-label={`Camera: ${camera ? cameraName(camera) : 'Select a camera'}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => onOpenChange(!open)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault()
            onOpenChange(true)
          }
        }}
        disabled={!cameras?.length}
      >
        <span>{camera ? cameraName(camera) : 'Select a camera'}</span>
      </button>
      <ChevronDown size={14} aria-hidden="true" />
      {open && (
        <MobilePicker
          mobile={mobile}
          label="Choose camera"
          trigger={trigger}
          onClose={() => onOpenChange(false)}
        >
          {mobile && (
            <div className="picker-heading">
              <strong>Camera</strong>
              <button
                aria-label="Close camera picker"
                onClick={() => onOpenChange(false)}
              >
                <X size={17} />
              </button>
            </div>
          )}
          <div
            className="camera-menu"
            id={id}
            role="menu"
            aria-label="Camera"
            onKeyDown={(event) => {
              const items = Array.from(
                event.currentTarget.querySelectorAll<HTMLButtonElement>('button'),
              )
              const index = items.indexOf(document.activeElement as HTMLButtonElement)
              let next: number
              if (event.key === 'ArrowDown') next = (index + 1) % items.length
              else if (event.key === 'ArrowUp')
                next = (index - 1 + items.length) % items.length
              else if (event.key === 'Home') next = 0
              else if (event.key === 'End') next = items.length - 1
              else return
              event.preventDefault()
              items[next]?.focus()
            }}
          >
            {cameras?.map((c) => (
              <button
                key={c.id}
                role="menuitemradio"
                aria-checked={c.id === selected}
                tabIndex={-1}
                onClick={() => {
                  onSelect(c.id)
                  onOpenChange(false)
                  trigger.current?.focus()
                }}
              >
                {cameraName(c)}
              </button>
            ))}
          </div>
        </MobilePicker>
      )}
    </div>
  )
}

interface ArchiveNavigationProps {
  previousDay: NavigationAction
  nextDay: NavigationAction
  calendar: ComponentProps<typeof Calendar>
  calendarOpen: boolean
  onCalendarOpenChange: (open: boolean) => void
  cameraPicker: CameraPickerProps
}

export function ArchiveNavigation({
  calendar,
  calendarOpen,
  onCalendarOpenChange,
  cameraPicker,
  previousDay,
  nextDay,
}: ArchiveNavigationProps) {
  const trigger = useRef<HTMLButtonElement>(null)
  const { selected: day, maxDay: today, onSelect: chooseDay } = calendar
  const stripEnd = day && addDays(day, 2) < today ? addDays(day, 2) : today
  const visibleDays = day
    ? Array.from({ length: 5 }, (_, i) => addDays(stripEnd, i - 4))
    : []

  return (
    <div className="day-navigation">
      <div className="archive-selectors">
        <div className="calendar-anchor">
          <button
            className={`calendar-trigger ${calendarOpen ? 'selected' : ''}`}
            ref={trigger}
            aria-label="Open calendar"
            aria-expanded={calendarOpen}
            onClick={() => {
              calendar.onMonth(monthOf(day))
              onCalendarOpenChange(!calendarOpen)
            }}
            disabled={!day}
          >
            <CalendarDays size={17} />
            <span>{day ? dateLabel(day) : 'Select a day'}</span>
            <ChevronDown size={14} />
          </button>
          {calendarOpen && day && (
            <MobilePicker
              mobile={cameraPicker.mobile}
              label="Choose recording date"
              trigger={trigger}
              onClose={calendar.onClose}
            >
              <Calendar {...calendar} modal={cameraPicker.mobile} />
            </MobilePicker>
          )}
        </div>
        <div className="calendar-day-skips">
          <button
            aria-label="Skip day backward"
            title="Skip day backward"
            {...previousDay}
          >
            <ChevronsLeft size={18} />
          </button>
          <button aria-label="Start of next day" title="Skip day forward" {...nextDay}>
            <ChevronsRight size={18} />
          </button>
        </div>
        <CameraPicker {...cameraPicker} />
      </div>
      <div className="day-strip">
        <button
          aria-label="Previous day"
          disabled={!day}
          onClick={() => chooseDay(addDays(day, -1))}
        >
          <ChevronLeft size={17} />
        </button>
        {visibleDays.map((d) => (
          <button
            key={d}
            className={`day-button ${d === day ? 'selected' : ''}`}
            onClick={() => chooseDay(d)}
            aria-label={`Select ${d}`}
            aria-pressed={d === day}
          >
            <span>
              {new Intl.DateTimeFormat('en-US', {
                weekday: 'short',
                timeZone: 'UTC',
              }).format(new Date(`${d}T12:00:00Z`))}
            </span>
            <strong>{Number(d.slice(-2))}</strong>
            <i className={calendar.data?.days[d] ? 'available' : ''} />
          </button>
        ))}
        <button
          aria-label="Next day"
          disabled={!day || day >= today}
          onClick={() => chooseDay(addDays(day, 1))}
        >
          <ChevronRight size={17} />
        </button>
      </div>
    </div>
  )
}
